/**
 * Prompt-size metrics for conversational-search observability (Phase 1).
 * Logged when NLP_PROMPT_TELEMETRY=true.
 */

export interface PromptTelemetryMetrics {
  promptIntentHint: string | null;
  /** Intent-routed system prompt only (no runtime context block). */
  systemPromptChars: number;
  systemPromptEstimatedTokens: number;
  /** Dynamic context appended to the system prompt (date, currentParams, suggestions). */
  contextMessageChars: number;
  contextMessageEstimatedTokens: number;
  /** systemPrompt + contextMessage (single system message sent to the provider). */
  fullSystemPromptChars: number;
  fullSystemPromptEstimatedTokens: number;
  /** Sum of all message contents sent to the provider (system + history + user). */
  totalMessageChars: number;
  totalMessageEstimatedTokens: number;
  conversationHistoryChars: number;
  conversationHistoryEstimatedTokens: number;
  userQueryChars: number;
  userQueryEstimatedTokens: number;
}

export const EMPTY_PROMPT_TELEMETRY: PromptTelemetryMetrics = {
  promptIntentHint: null,
  systemPromptChars: 0,
  systemPromptEstimatedTokens: 0,
  contextMessageChars: 0,
  contextMessageEstimatedTokens: 0,
  fullSystemPromptChars: 0,
  fullSystemPromptEstimatedTokens: 0,
  totalMessageChars: 0,
  totalMessageEstimatedTokens: 0,
  conversationHistoryChars: 0,
  conversationHistoryEstimatedTokens: 0,
  userQueryChars: 0,
  userQueryEstimatedTokens: 0,
};

/** Fast token estimate for observability only (not billing). */
export const estimateTokenCount = (content: string): number => {
  if (!content) {
    return 0;
  }
  return Math.ceil(content.length / 4);
};

export function measurePromptTelemetry(input: {
  systemPrompt: string;
  contextMessage: string;
  userQuery: string;
  promptIntentHint?: string | null;
  recentMessages?: Array<{ role: 'user' | 'assistant'; content: string }>;
}): PromptTelemetryMetrics {
  const { systemPrompt, contextMessage, userQuery, recentMessages, promptIntentHint } = input;
  const fullSystemPrompt = systemPrompt + contextMessage;

  const conversationHistoryChars =
    recentMessages?.reduce((sum, message) => sum + message.content.length, 0) ?? 0;

  const userQueryChars = userQuery.length;
  const totalMessageChars =
    fullSystemPrompt.length + conversationHistoryChars + userQueryChars;

  return {
    promptIntentHint: promptIntentHint ?? null,
    systemPromptChars: systemPrompt.length,
    systemPromptEstimatedTokens: estimateTokenCount(systemPrompt),
    contextMessageChars: contextMessage.length,
    contextMessageEstimatedTokens: estimateTokenCount(contextMessage),
    fullSystemPromptChars: fullSystemPrompt.length,
    fullSystemPromptEstimatedTokens: estimateTokenCount(fullSystemPrompt),
    totalMessageChars,
    totalMessageEstimatedTokens: estimateTokenCount(
      fullSystemPrompt +
        (recentMessages?.map(m => m.content).join('') ?? '') +
        userQuery
    ),
    conversationHistoryChars,
    conversationHistoryEstimatedTokens: estimateTokenCount(
      recentMessages?.map(m => m.content).join('') ?? ''
    ),
    userQueryChars,
    userQueryEstimatedTokens: estimateTokenCount(userQuery),
  };
}

/** Flatten metrics into a telemetry payload (every event carries prompt size). */
export function promptTelemetryPayload(
  metrics: PromptTelemetryMetrics
): Record<string, number | string | null> {
  return { ...metrics };
}

/** Short-circuit paths: no LLM prompt, but still record user query size. */
export function promptTelemetryForQueryOnly(userQuery: string): PromptTelemetryMetrics {
  return {
    ...EMPTY_PROMPT_TELEMETRY,
    userQueryChars: userQuery.length,
    userQueryEstimatedTokens: estimateTokenCount(userQuery),
  };
}
